import { useAuth } from '@clerk/clerk-react';
import { useCallback, useEffect, useRef, useState } from 'react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';
const HEARTBEAT_INTERVAL_MS = 10_000;

/**
 * phase2-interfaces.md §2.4 — streaming-lock client hook.
 *
 * Lifecycle owned by this hook:
 *
 *   1. `acquire()` — POST /interviews/:id/acquire-stream. On success starts
 *      a 10s heartbeat loop and updates `state` to `'held'`. On contention
 *      stores the existing holder's `heldSince` and updates `state` to
 *      `'contended'` so the UI can render the takeover banner.
 *   2. `takeover()` — POST /interviews/:id/takeover-stream. Always succeeds
 *      per decision Q5 (uniform takeover semantics). Restarts the heartbeat
 *      loop with the fresh lockToken.
 *   3. Heartbeat loop — every 10s, POST /interviews/:id/heartbeat-stream
 *      with `X-Stream-Lock`. On 409 (`lock_lost` or `stream_lock_not_held`)
 *      stops the loop and flips state to `'lost'` — the UI renders an
 *      "Opened in another window" notice and disables the input.
 *   4. `release()` — best-effort `DEL` via the /pause / /end endpoints
 *      (server side handles deletion); locally just stops the heartbeat
 *      loop and resets state.
 *
 * The hook is a no-op when `enabled = false` (legacy traffic). Components
 * pass `enabled = isPhase2Active(...)` to opt in.
 */
export type StreamLockState =
  | { status: 'idle' }
  | { status: 'acquiring' }
  | { status: 'held'; lockToken: string }
  | { status: 'contended'; heldSince: string }
  | { status: 'lost' }
  | { status: 'error'; message: string };

/**
 * Synchronous return value of `acquire()` / `takeover()`. Mirrors the
 * resolved `StreamLockState` so a caller racing against React's render
 * cycle can read the freshly-minted `lockToken` *before* the next render
 * — the missing piece that made the original "acquire, then immediately
 * send" call site (Interview.tsx → useInterviewStream) hit 400 because
 * the hook's React state had not yet propagated when send() ran.
 */
export type AcquireResult =
  | { status: 'held'; lockToken: string }
  | { status: 'contended'; heldSince: string }
  | { status: 'lost' }
  | { status: 'error'; message: string };

export interface UseStreamLockOptions {
  sessionId: string | null;
  /** Admin "test as user" target id, threaded through to the API. */
  targetUserId?: string | null;
  /** When false, the hook is dormant — used to keep legacy traffic clean. */
  enabled: boolean;
}

export interface UseStreamLockResult {
  state: StreamLockState;
  /** Returns the lockToken when `state.status === 'held'`, else null. */
  lockToken: string | null;
  acquire: () => Promise<AcquireResult>;
  takeover: () => Promise<AcquireResult>;
  release: () => void;
}

export function useStreamLock(options: UseStreamLockOptions): UseStreamLockResult {
  const { sessionId, targetUserId, enabled } = options;
  const { getToken } = useAuth();

  const [state, setState] = useState<StreamLockState>({ status: 'idle' });
  // Stable client id for the lifetime of the component mount — used in the
  // takeover banner copy ("opened in another window") and as a debug
  // breadcrumb in server logs.
  const clientIdRef = useRef<string>(crypto.randomUUID());
  const heartbeatRef = useRef<number | null>(null);

  const stopHeartbeat = useCallback(() => {
    if (heartbeatRef.current !== null) {
      window.clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  }, []);

  // Always cancel the heartbeat on unmount or when the session changes —
  // a leftover interval would beat against the wrong session id.
  useEffect(() => {
    return () => stopHeartbeat();
  }, [stopHeartbeat]);

  const post = useCallback(
    async <T>(
      path: string,
      body: Record<string, unknown>,
      extraHeaders: Record<string, string> = {}
    ): Promise<{ ok: boolean; status: number; data: T | null; errCode?: string }> => {
      const token = await getToken();
      const res = await fetch(`${API_URL}${path}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...extraHeaders,
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        return {
          ok: false,
          status: res.status,
          data: null,
          errCode: errBody?.error?.code,
        };
      }
      const json = await res.json().catch(() => ({}));
      return { ok: true, status: res.status, data: (json?.data ?? null) as T | null };
    },
    [getToken]
  );

  const startHeartbeat = useCallback(
    (lockToken: string) => {
      stopHeartbeat();
      heartbeatRef.current = window.setInterval(async () => {
        if (!sessionId) return;
        const res = await post<{ ok: boolean; ttlSeconds: number }>(
          `/api/v1/interviews/${sessionId}/heartbeat-stream`,
          targetUserId ? { targetUserId } : {},
          { 'X-Stream-Lock': lockToken }
        );
        if (!res.ok && res.status === 409) {
          stopHeartbeat();
          setState({ status: 'lost' });
        }
      }, HEARTBEAT_INTERVAL_MS);
    },
    [sessionId, targetUserId, post, stopHeartbeat]
  );

  const acquire = useCallback(async (): Promise<AcquireResult> => {
    if (!enabled || !sessionId) {
      return { status: 'error', message: 'Stream lock disabled or no session' };
    }
    setState({ status: 'acquiring' });
    const res = await post<
      | { acquired: true; lockToken: string; ttlSeconds: number }
      | { acquired: false; heldSince: string; canTakeover: boolean }
    >(`/api/v1/interviews/${sessionId}/acquire-stream`, {
      clientId: clientIdRef.current,
      ...(targetUserId ? { targetUserId } : {}),
    });
    if (!res.ok || !res.data) {
      const result: AcquireResult = {
        status: 'error',
        message: 'Could not acquire stream lock',
      };
      setState(result);
      return result;
    }
    if (res.data.acquired) {
      const result: AcquireResult = { status: 'held', lockToken: res.data.lockToken };
      setState(result);
      startHeartbeat(res.data.lockToken);
      return result;
    }
    const result: AcquireResult = { status: 'contended', heldSince: res.data.heldSince };
    setState(result);
    return result;
  }, [enabled, sessionId, targetUserId, post, startHeartbeat]);

  const takeover = useCallback(async (): Promise<AcquireResult> => {
    if (!enabled || !sessionId) {
      return { status: 'error', message: 'Stream lock disabled or no session' };
    }
    setState({ status: 'acquiring' });
    const res = await post<{ acquired: true; lockToken: string; ttlSeconds: number }>(
      `/api/v1/interviews/${sessionId}/takeover-stream`,
      {
        clientId: clientIdRef.current,
        ...(targetUserId ? { targetUserId } : {}),
      }
    );
    if (!res.ok || !res.data) {
      const result: AcquireResult = {
        status: 'error',
        message: 'Could not take over stream',
      };
      setState(result);
      return result;
    }
    const result: AcquireResult = { status: 'held', lockToken: res.data.lockToken };
    setState(result);
    startHeartbeat(res.data.lockToken);
    return result;
  }, [enabled, sessionId, targetUserId, post, startHeartbeat]);

  const release = useCallback(() => {
    stopHeartbeat();
    setState({ status: 'idle' });
  }, [stopHeartbeat]);

  // Reset state when the session changes — leftover lockTokens belong to a
  // different session and would silently mis-author /message requests.
  useEffect(() => {
    stopHeartbeat();
    setState({ status: 'idle' });
  }, [sessionId, stopHeartbeat]);

  const lockToken = state.status === 'held' ? state.lockToken : null;
  return { state, lockToken, acquire, takeover, release };
}
